import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-expo";
import { Link, useFocusEffect } from "expo-router";
import { ActivityIndicator, FlatList, Pressable, StyleSheet, Text, View } from "react-native";

import { fetchSessions, type Session } from "../../src/api";
import { SessionRow } from "../../src/session-row";
import { colour, type } from "../../src/theme";

/**
 * Your plays (WI-039).
 *
 * The first screen that shows the record rather than the catalogue or the
 * shelf. `fetchSessions` has existed since WI-036 with nothing calling it --
 * plays went in and were never shown back.
 */
export default function PlaysScreen() {
  const { isLoaded, isSignedIn } = useAuth();
  const [items, setItems] = useState<Session[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!isLoaded || !isSignedIn) return () => {};
    const controller = new AbortController();

    setError(null);
    fetchSessions(controller.signal)
      .then((found) => {
        if (!controller.signal.aborted) setItems(found);
      })
      .catch((cause: Error) => {
        if (controller.signal.aborted || cause.name === "AbortError") return;
        setError(cause.message);
      });

    return () => controller.abort();
  }, [isLoaded, isSignedIn]);

  useEffect(load, [load]);

  // A play logged on a game page should be here when you come back, not on
  // the next cold start.
  useFocusEffect(load);

  /**
   * Applied locally rather than by refetching.
   *
   * The row knows what it sent and the API applied it or failed, so a round
   * trip would only confirm what is already known -- and would throw away the
   * scroll position on a list that could be long.
   */
  const replace = useCallback((next: Session) => {
    setItems((current) =>
      current === null ? current : current.map((s) => (s.id === next.id ? next : s)),
    );
  }, []);

  const drop = useCallback((id: string) => {
    setItems((current) => (current === null ? current : current.filter((s) => s.id !== id)));
  }, []);

  if (!isLoaded) return null;

  if (!isSignedIn) {
    return (
      <View style={styles.centre}>
        <Text style={styles.blurb}>Your plays are yours, so you need to be signed in.</Text>
        <Link href="/sign-in" asChild>
          <Pressable style={styles.button}>
            <Text style={styles.buttonText}>Sign in</Text>
          </Pressable>
        </Link>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      {error !== null && <Text style={styles.error}>{error}</Text>}

      {items === null && error === null && <ActivityIndicator style={styles.spinner} />}

      {items !== null && items.length === 0 && (
        <Text style={styles.blurb}>
          No plays yet. Find a game and press Played — one tap is a whole record.
        </Text>
      )}

      <FlatList
        data={items ?? []}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.list}
        renderItem={({ item }) => (
          <SessionRow session={item} onSaved={replace} onDeleted={drop} />
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, padding: 16 },
  centre: { flex: 1, alignItems: "center", justifyContent: "center", padding: 24, gap: 12 },
  spinner: { marginTop: 24 },
  blurb: { color: colour.inkMuted, ...type.meta, textAlign: "center" },
  error: { color: colour.danger, ...type.meta, marginBottom: 8 },
  list: { gap: 12, paddingBottom: 24 },
  button: {
    backgroundColor: colour.ink,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  buttonText: { color: colour.surface, ...type.body, fontWeight: "600" },
});
